<template>
    <input-base
    :params="params"
    :rightButtonIcon="rightButtonIcon"
    :inputFocused="inputFocused"
    :inputValue="inputValue"
    @rightButtonClicked="emit('rightButtonClicked')"
    @tooltipLeftButtonClicked="emit('tooltipLeftButtonClicked')"
    @tooltipRightButtonClicked="emit('tooltipRightButtonClicked')">
        <!-- Input slot -->
        <template v-slot:input>
            <input
            type="text"
            v-mask="params.mask"
            ref="inputRef"
            :autocomplete="applyAutoCompleteOneTime ? 'one-time-code' : 'off'"
            :class="{'apply-dark-background-variant': params.darkBackgroundVariant, 'error-state': params.errorState }"
            :value="params.ourMask?.visualMask ? params.ourMask.visualMask(inputValue) : inputValue"
            :placeholder="params.placeholder"
            :inputmode=" applyNumbersIntInputMode ? 'numeric' : 'text'"
			@keyup="checkIfReturnMobileClick"
            @focus="[emit('onFocus'), inputFocused = true, ourMaskInitialValueHandler()]"
            @blur="[emit('onBlur'), inputFocused = false, maskedOnBlurHandler()]"
            @keyup.enter="emit('onEnter')"
            @keydown.backspace="backspaceHandler"
            @paste="pasteHandler"
            @input="handleInputChange">
        </template>
    </input-base>
</template>

<script setup>
/* Components */
import InputBase from '@/components/generic/inputs/Base'

/* Vue Imports */
import { ref, watch, computed, onMounted } from 'vue'
import { ACCOUNTS_NIB_IBAN, IBAN_AO } from '@/assets/constants/masks'

/* Emmitters */
const emit = defineEmits(['onFocus', 'onBlur', 'onEnter', 'input', 'inputPasteData', 'rightButtonClicked', 'tooltipLeftButtonClicked', 'tooltipRightButtonClicked'])

/** props received
@params {Object} - Object with:
    - labelText {String}: label for textField
    - placeholder {String}: text for placholder
    - darkBackgroundVariant {Boolean}: property to apply specific colors for blue backgrounds
    - bottomMessage {String}: text to display in bottom elements
    - errorMessage {String}: text to display in bottom elements while in errorState
    - showCounter {Boolean}: property to display char counter
    - maxLength {Number}: maxlength for input
    - mandatory { Boolean } adds * to labelText
    - errorState {Boolean}: property to show if input has error or not
    - mask {Object, String}: mask for input
    - ourMask {Object}: use this mask if maska doesnt accept your regex, Object with:
        - regex {Regex}
        - validatorFn {Function} a function specific for validating regex
        - parseInputText {Function} a function that removes unwanted chars from your text in accord with regex
    - tooltipInfo {Object} with:
        - title {String} - tooltip title
        - text {String} - tooltip text
        - description {String} - tooltip description
        - negative {Boolean} - prop to apply negative styles
        - size {String} can be [medium, small, tiny (default)]
        - leftButtonText {String} - text for left button, has default text if this is not passed
        - rightButtonText {String} - text for right button, has default text if this is not passed
        - applyPositionFixed {Boolean} - prop to force tooltipo to use fixed position
    - tooltipIcon {String} - icon name to trigger tooltip
    - applyNumbersIntInputMode {Boolean} - to apply numeric input mode
@rightButtonIcon { String } Icon to show right button
@applyAutoCompleteOneTime {Boolean} - prop to apply one-time-code to autocomplete input
@inputValue {String, Number}: value to be displayed in input
@focusInput {Boolean} - prop to trigger input focus
*/
const props = defineProps({
    params: Object,
    rightButtonIcon: String,
    applyAutoCompleteOneTime: Boolean,
    inputValue: [String, Number],
    focusInput: Boolean
})

const inputFocused = ref(false)
const inputRef = ref('')

const applyNumbersIntInputMode = computed(() => {
    if (props.params.applyNumbersIntInputMode) return true

    /* If we receive a mask is only numbers */
    if (props.params && props.params.mask) {
        const maskValue = props.params.mask.mask

        switch (maskValue) {
            case '9{14}': /* Phonenumber mask */
                return true
            default:
                return false
        }
    }
    return false
})

const ourMaskInitialValueHandler = () => {
    if (props.params?.ourMask?.initialValue && props.inputValue.length < props.params?.ourMask.initialValue.length) {
        emit('input', props.params?.ourMask.initialValue)
    }
}

const maskedOnBlurHandler = () => {
    /* This is a necessary measury because v-mask has a known bug handling backspace when a mask like our IBAN_AO is applied. */
    if (props.params.mask && props.params.mask.mask === IBAN_AO.mask && props.inputValue === 'AO06') emit('input', props.params.mask.unmaskFn(''))
    /* If on blur the only thing in inputValue is our initialValue we will empty our input */
    if (props.params?.ourMask?.initialValue && props.inputValue === props.params?.ourMask?.initialValue) emit('input', '')
}

const backspaceHandler = $event => {
    // This is a necessary measury because v-mask has a known bug handling backspace when a mask like our ACCOUNTS_NIB_IBAN is applied.
    if (props.params.mask && props.params.mask.mask === ACCOUNTS_NIB_IBAN.mask && props.inputValue === 'AO06') emit('input', props.params.mask.unmaskFn(''))
    /* If on backspace the only thing in inputValue is our initialValue we will empty our input */
    if (props.params?.ourMask?.initialValue && props.inputValue === props.params?.ourMask?.initialValue) {
        $event.stopPropagation()
    }
}

const pasteHandler = $event => {
    if (props.params?.ourMask?.initialValue && $event.clipboardData.getData('text').includes(props.params.ourMask.initialValue)) {
        emit('input', $event.clipboardData.getData('text'))
    } else if (props.params.pasteParser) {
        emit('inputPasteData', props.params.pasteParser($event.clipboardData.getData('text')))
    }
}

const regexMaxLengthValidator = string => {
	const regex = new RegExp(`^.{0,${props.params.maxLength}}$`)

	return regex.test(string)
}

const checkIfReturnMobileClick = $event => {
	if ($event.which === 13) document.activeElement.blur()
}

const handleInputChange = $event => {
    if (props.params.ourMask) handleOurMask($event)
    else if (props.params.mask && props.params.mask.unmaskFn) {
        /* We are saving our caret position to replace our caret after we changing the value */
        const caretPosition = $event.target.selectionEnd
        /* This setTimeout is to fix value being empty when chosing text from clipboard suggestions */
        setTimeout(() => {
            emit('input', props.params.mask.unmaskFn($event.target.value))
            setTimeout(() => { $event.target.setSelectionRange(caretPosition, caretPosition) })
        })
    } else if (props.params.maxLength && !regexMaxLengthValidator($event.target.value)) handleMaxLength($event)
    else emit('input', props.params.mask ? $event.target.inputmask.unmaskedvalue() : $event.target.value)
}

const handleOurMask = $event => {
    const caretPosition = $event.target.selectionEnd
    const caretIsAtEndOfString = $event.target.selectionEnd === $event.target.value.length
    const regex = props.params.ourMask.regex /* Always passed to ourMarsk validator, even if it doesnt exists, some validators might need it */
    let text = $event.target.value

    /* First we validate if text written is valid */
    if (props.params.ourMask.validatorFn && !props.params.ourMask.validatorFn($event.target.value, regex)) {
        /* If we have parseInputText function we will need to parse our text before validating it */
        text = props.params.ourMask.parseInputText ? props.params.ourMask.parseInputText($event.target.value) : $event.target.value
        /* we will cut our text till it becomes valid */
        while (!props.params.ourMask.validatorFn(text, regex) && text.length !== 0) text = text.slice(0, -1)
    } else if (props.params.ourMask.parseInputText) {
        text = props.params.ourMask.parseInputText($event.target.value)
    }

    if (props.params.maxLength && !regexMaxLengthValidator($event.target.value)) {
        handleMaxLength($event)
        return
    }

    const valueMasked = props.params.ourMask.visualMask ? props.params.ourMask.visualMask(text) : null
    $event.target.value = valueMasked || text

    emit('input', text)
    /* Placing caret in the right position */
    if (!caretIsAtEndOfString) {
        $event.target.selectionEnd = caretPosition
    }
}

/*
* Some mobile devices ignore maxLength prop from input
* Becuz of that we will force on our side to cut the value to match the maxLength provided
*/
const handleMaxLength = $event => {
    let text = $event.target.value
    while (!regexMaxLengthValidator(text) && text.length !== 0) text = text.slice(0, -1)

    $event.target.value = text
    emit('input', props.params.ourMask?.parseInputText ? props.params.ourMask.parseInputText($event.target.value) : $event.target.value)
}

watch(() => props.inputValue, (newVal, oldVal) => {
    if (newVal !== oldVal && props.params?.errorState) {
        emit('input', props.inputValue)
    }

    if (newVal.length && props.params && props.params.maxLength && newVal.length > props.params.maxLength) {
        const newVal = props.inputValue.slice(0, props.params.maxLength)
        emit('input', newVal)
    }
})

onMounted(() => {
    if (props.focusInput) inputRef.value.focus()
})
</script>

<style lang="scss" scoped>
input {
    width: 100%;
    height: 40px;

    @include H6-Regular;
    line-height: 18px;
    color: var(--secondary-dark-3);
}
</style>
